import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useQuery, useQueryClient } from "react-query";

import { isForecastTrumpingSource } from "constants/settings.constants";
import { ColorLockedLegendItem } from "store/features";
import {
  selectOthersMembersUnavailable,
  useOthersMembersAvailabilityStore
} from "stores/othersMembersAvailabilityStore";
import {
  applyFieldNameMode,
  isCustomFieldProperty
} from "utils/column/getColumnLabelWithContext";
import { buildForecastTrumpingSettings } from "utils/data-table";

import {
  BIN_SEARCH_DEBOUNCE_MS,
  OthersMembersRequest,
  fetchBinSearch,
  isSearchWithinLimit,
  markUnavailableOn404,
  toBinSearchBin,
  toGroupByPdenSource
} from "api/othersMembers";

import { useProperty } from "hooks";
import useDebounce from "hooks/useDebounce";
import { useMaxBinDefaults } from "hooks/useMaxBinDefaults";

import { LegendItemModel } from "models";
import { EntityKind } from "models/entityKind";

import toLegendItem from "../toLegendItem";
import { GroupByFilterDependencies, UseLegendControllerResult } from "../types";
import { checkedItemKey, collapsedCheckedItems } from "../util/collapsedCheckedItems";
import { buildColorLockKey } from "../util/colorLockKey";
import { useIsLegendBehindFilter } from "./useIsLegendBehindFilter";

export interface UseLegendControllerProps {
  deps: GroupByFilterDependencies;
  entityKind: EntityKind;
  isForecastToggleOn: boolean;
  selectedForecastFolder: string;
  checkedLegendItemsRef: MutableRefObject<LegendItemModel[]>;
  replacePlayChecked: (item: LegendItemModel) => void;
  // Checked-Item Filtering's stable handler for "date range changed" —
  // replaces the former performBatchOption("uncheckAll") + manual ref-clear +
  // subject.next trio; that whole uncheck-and-reapply sequence is now
  // internal to it.
  onDateRangeChanged: () => void;
  // Private-latest-accessor setter Checked-Item Filtering owns (useLatest
  // pattern) — Legend reports its current filtered list here so batch ops
  // there can read it at click-time. Not a ref on either interface.
  reportFilteredItems: (items: LegendItemModel[]) => void;
  lockedColorsInStore: Record<string, ColorLockedLegendItem>;
  lastSeenDateRangeByPropertyRef: MutableRefObject<Record<string, string>>;
  // Checked-Item Filtering's batch-select, invoked on Enter in the focus
  // search. Lives there (not here) since bulk-checking the filtered set is
  // its concern, not the legend/search state this hook owns.
  onEnterSelect: () => void;
  // The tree map has no legend list to search.
  showTreeMap: boolean;
}

// Case-insensitive substring, the same match bin-search applies, so the fallback and the
// server agree on what a term finds.
function search(searchTerm: string, items: LegendItemModel[]): LegendItemModel[] {
  const term = searchTerm.trim().toLowerCase();
  if (term.length === 0) {
    return items;
  }
  return items.filter((item) => (item.value ?? "").toLowerCase().includes(term));
}

// data-svc-rs's MAX_LIMIT, so a term under the cap resolves in at most two requests.
const LEGEND_SEARCH_PAGE_SIZE = 200;
// More matches than this can't be shown usefully, so the term falls back to the client search.
const MAX_LEGEND_SEARCH_TITLES = 250;

type LegendSearchRequest = Omit<
  OthersMembersRequest,
  "search" | "offset" | "limit" | "view"
>;

async function fetchLegendTitles(
  request: LegendSearchRequest,
  term: string,
  signal?: AbortSignal
): Promise<string[] | null> {
  const titles: string[] = [];
  try {
    for (;;) {
      const response = await fetchBinSearch(
        {
          ...request,
          view: "legend",
          search: term,
          offset: titles.length,
          limit: LEGEND_SEARCH_PAGE_SIZE
        },
        signal
      );
      // A partial list would hide legend rows, so the caller uses the client search instead.
      if (response.truncated || response.total > MAX_LEGEND_SEARCH_TITLES) return null;
      titles.push(...response.members.map((member) => member.title));
      if (
        response.members.length < LEGEND_SEARCH_PAGE_SIZE ||
        titles.length >= response.total
      ) {
        return titles;
      }
    }
  } catch (error) {
    markUnavailableOn404(error);
    throw error;
  }
}

// Server titles keep the legend's own order by filtering the legend rows, not by
// reordering to the server's ranking. The Others row is placed separately.
function applySearch(
  term: string,
  items: LegendItemModel[],
  serverTitles: string[] | null | undefined
): LegendItemModel[] {
  if (!term || !serverTitles) {
    return search(term, items);
  }
  const matched = new Set(serverTitles);
  // The Others row's label and empty placeholder bins aren't values the server's search
  // knows, so they match on their own label.
  return items.filter((item) =>
    item.isOthersRollup || item.count === 0
      ? search(term, [item]).length > 0
      : matched.has(item.value)
  );
}

// EVA-6412 SR-004b (NEW, T027) — owns legendItems/filteredItems/legendTitle,
// the toLegendItem effect, and search/focus-search filtering, lifted verbatim
// out of GroupByFilterActivity.tsx. Reads forecast/checked values via ARGS
// (threaded by the controller) to keep the straddling effect whole.
export const useLegendController = ({
  deps,
  entityKind,
  isForecastToggleOn,
  selectedForecastFolder,
  checkedLegendItemsRef,
  replacePlayChecked,
  onDateRangeChanged,
  reportFilteredItems,
  lockedColorsInStore,
  lastSeenDateRangeByPropertyRef,
  onEnterSelect,
  showTreeMap
}: UseLegendControllerProps): UseLegendControllerResult => {
  const {
    groupBy,
    activeLegendItems,
    polygonFilter,
    propertiesFilter,
    useNormalizeBy,
    normalizeBy,
    fullFilterState,
    checkedGlobalForecasts,
    specialForecastFolders,
    forecastReserveCategory,
    syncWells,
    updatePlayLegend,
    isWell
  } = deps;

  const groupByTitle = useProperty(
    groupBy.property,
    isForecastToggleOn,
    selectedForecastFolder,
    entityKind,
    groupBy.performanceDateRange
  );

  // Built once per input change and only when the group-by is on Forecast Trumping, so a
  // forecast checkbox click on an explicit-source group-by does not rebuild the legend for an
  // identical result. Stable `undefined` otherwise.
  const legendTrumpingSettings = useMemo(
    () =>
      isForecastTrumpingSource(groupBy.forecastFolder)
        ? buildForecastTrumpingSettings(
            checkedGlobalForecasts,
            specialForecastFolders,
            syncWells?.isSync ? syncWells.schema : undefined,
            syncWells?.isSync ? syncWells.forecastRescat : undefined,
            forecastReserveCategory
          )
        : undefined,
    [
      groupBy.forecastFolder,
      checkedGlobalForecasts,
      specialForecastFolders,
      syncWells,
      forecastReserveCategory
    ]
  );

  const maxBinDefaultsEnabled = useMaxBinDefaults();
  // Every item that has written a checked predicate, so one collapsed into
  // Others can still be kept after its row is gone (even across a focus switch).
  const rememberedCheckedItemsRef = useRef(new Map<string, LegendItemModel>());
  // The part of checkedLegendItemsRef with no visible row.
  const collapsedCheckedItemsRef = useRef<LegendItemModel[]>([]);

  const [legendItems, setLegendItems] = useState<LegendItemModel[]>([]);
  const [filteredItems, setFilteredItems] = useState<LegendItemModel[]>([]);
  const [legendTitle, setLegendTitle] = useState("");
  const [focusSearch, setFocusSearch] = useState("");
  // Matching runs synchronously per keystroke — debounce the value
  // that actually drives filtering so typing stays responsive; the input
  // itself stays bound to the live (non-debounced) focusSearch.
  const debouncedFocusSearch = useDebounce(focusSearch, 150);
  const serverFocusSearch = useDebounce(focusSearch, BIN_SEARCH_DEBOUNCE_MS);

  // The category prefix alone fills the header (EVA-339); legendTitle keeps it for the hover.
  const legendDisplayTitle = useMemo(
    () =>
      isCustomFieldProperty(groupBy.property)
        ? applyFieldNameMode(legendTitle, groupBy)
        : legendTitle,
    [legendTitle, groupBy]
  );

  const queryClient = useQueryClient();
  const isBinSearchUnavailable = useOthersMembersAvailabilityStore(
    selectOthersMembersUnavailable
  );
  const isTrumping = isForecastTrumpingSource(groupBy.forecastFolder);
  const hasRequestShape =
    maxBinDefaultsEnabled === true &&
    !isBinSearchUnavailable &&
    !showTreeMap &&
    !groupBy.mode &&
    !isTrumping &&
    !groupBy.performanceDateRange &&
    !!deps.filterId.id &&
    !!groupBy.bin;
  const searchRequest = useMemo<LegendSearchRequest | undefined>(
    () =>
      hasRequestShape
        ? {
            filterId: deps.filterId.id,
            property: groupBy.property,
            bin: toBinSearchBin(groupBy.bin),
            entityKind,
            pdenSource: toGroupByPdenSource(groupBy.pdenSource),
            normalizeBySetting: useNormalizeBy ? normalizeBy : null,
            showGroupsNotInFilter: deps.showGroupsNotInFilter,
            sortBy: fullFilterState?.sortBy
          }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasRequestShape,
      deps.filterId.id,
      groupBy.property,
      groupBy.bin,
      groupBy.pdenSource,
      entityKind,
      useNormalizeBy,
      normalizeBy,
      deps.showGroupsNotInFilter,
      fullFilterState?.sortBy
    ]
  );
  const isServerSearchable = (term: string) =>
    !!searchRequest && term.trim() !== "" && isSearchWithinLimit(term);
  // The term is in the key, so a response for an older term can never land on a newer one.
  const binSearchOptions = (term: string) => ({
    queryKey: ["legendBinSearch", searchRequest, term],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchLegendTitles(searchRequest as LegendSearchRequest, term, signal),
    staleTime: 60_000,
    retry: false
  });
  // Asks only while the term is settled: typing on moves the query off the old
  // term, and react-query aborts its request once nothing observes it.
  const serverTerm = focusSearch === serverFocusSearch ? serverFocusSearch : "";
  const binSearchQuery = useQuery({
    ...binSearchOptions(serverTerm),
    enabled: isServerSearchable(serverTerm)
  });
  const isSearchPending =
    isServerSearchable(focusSearch) &&
    (focusSearch !== serverFocusSearch || binSearchQuery.isFetching);
  // While loading, after a failure, or until the server term catches up with the
  // client one, the client search stands in, with no toast.
  const serverTitles =
    binSearchQuery.isSuccess && serverTerm === debouncedFocusSearch
      ? binSearchQuery.data
      : undefined;

  function setFilteredItemsAndUseLockedColors(items: LegendItemModel[]) {
    const itemsWithLockedColors = items.map((item) => {
      const key = buildColorLockKey(item.property, item.value);
      const found = lockedColorsInStore[key];
      if (!found) {
        return item;
      }
      return Object.assign({}, item, {
        color: found.color,
        isColorLocked: true
      });
    });
    setFilteredItems(itemsWithLockedColors);
    reportFilteredItems(itemsWithLockedColors);
  }

  useEffect(() => {
    setFocusSearch("");
  }, [groupBy.property]);

  const isLegendBehindFilter = useIsLegendBehindFilter(entityKind, deps.filterId.id);

  useEffect(() => {
    if (isLegendBehindFilter) return;
    if (!activeLegendItems.length) {
      setLegendItems([]);
      setFilteredItems([]);
      reportFilteredItems([]);
      return;
    }

    const propertySplit = groupBy.property.split(".");
    let title = groupBy.title;
    if (groupByTitle) {
      title = groupByTitle.fullContextTitleWithNormalization;
    }
    if (propertySplit.length === 3) {
      if (propertySplit[0] === "QC_Meta") {
        title = `${groupBy.subgroup}` + title;
      }
    }

    const normalizeBySettings = Object.assign({}, normalizeBy, { useNormalizeBy });
    setLegendTitle(title);
    const { legendItems: computedLegendItems, matchedPredicateIndices } = toLegendItem(
      activeLegendItems,
      legendTrumpingSettings
        ? { ...groupBy, forecastTrumpingSettings: legendTrumpingSettings }
        : groupBy,
      propertiesFilter,
      groupByTitle,
      normalizeBySettings
    );

    setLegendItems(computedLegendItems);
    const filtered = applySearch(debouncedFocusSearch, computedLegendItems, serverTitles);
    setFilteredItemsAndUseLockedColors(filtered);
    const visibleCheckedItems = computedLegendItems.filter((item) => item.isChecked);
    if (maxBinDefaultsEnabled === true) {
      for (const item of checkedLegendItemsRef.current ?? []) {
        if (item.retainedFilter) continue;
        rememberedCheckedItemsRef.current.set(
          checkedItemKey(item.property, item.value),
          item
        );
      }
      collapsedCheckedItemsRef.current = collapsedCheckedItems(
        groupBy,
        propertiesFilter,
        matchedPredicateIndices,
        rememberedCheckedItemsRef.current
      );
      checkedLegendItemsRef.current = [
        ...visibleCheckedItems,
        ...collapsedCheckedItemsRef.current
      ];
    } else {
      // An Exclude with no row is kept as written, or the next write of the checked set
      // would drop it.
      collapsedCheckedItemsRef.current = collapsedCheckedItems(
        groupBy,
        propertiesFilter,
        matchedPredicateIndices,
        new Map()
      ).filter((item) => item.isNotEqual);
      checkedLegendItemsRef.current = [
        ...visibleCheckedItems,
        ...collapsedCheckedItemsRef.current
      ];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLegendBehindFilter,
    groupBy,
    activeLegendItems,
    polygonFilter,
    propertiesFilter,
    useNormalizeBy,
    normalizeBy,
    groupByTitle,
    fullFilterState,
    entityKind,
    legendTrumpingSettings
  ]);

  // Fabricate a legend item for plays selected outside the legend (e.g. the map
  // toolbar play selector) — Well-only, gated on the resolved isWell flag, not
  // a re-derivation of entityKind (research.md R2 domain-asymmetry note).
  useEffect(() => {
    if (!updatePlayLegend.length) return;
    const alreadyChecked = checkedLegendItemsRef.current?.some(
      (item) => item.property === "Header.ResourcePlay" && item.value === updatePlayLegend
    );
    if (alreadyChecked) return;
    if (isWell) {
      const legend = new LegendItemModel(
        `(0) ${updatePlayLegend}`,
        "Header.ResourcePlay",
        false,
        1,
        {
          BinSize: "",
          LessThan: "",
          GreaterThan: "",
          MinSize: "",
          MaxBins: "",
          MaxBinsSortOrder: "WellCount"
        },
        true,
        0
      );
      legend.color = "";
      legend.fontColor = "";
      legend.isChecked = true;
      legend.isNotEqual = false;
      legend.value = updatePlayLegend;
      legend.text = `(0) ${updatePlayLegend}`;
      replacePlayChecked(legend);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePlayLegend]);

  // Keyed by property so switching fields and back correctly detects a date
  // range change (uses the transient lastSeenDateRangeByPropertyRef — P5 kept ref).
  useEffect(() => {
    if (!groupBy.property.includes("DateRange")) return;

    const currKey = groupBy.performanceDateRange
      ? `${groupBy.performanceDateRange.startDate}|${groupBy.performanceDateRange.endDate}`
      : "";
    const prevKey = lastSeenDateRangeByPropertyRef.current[groupBy.property] ?? "";
    lastSeenDateRangeByPropertyRef.current[groupBy.property] = currKey;

    if (!prevKey || prevKey === currKey) return;

    if (checkedLegendItemsRef.current.length > 0) {
      onDateRangeChanged();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy.performanceDateRange, groupBy.property]);

  useEffect(() => {
    const filtered = applySearch(debouncedFocusSearch, legendItems, serverTitles);
    setFilteredItemsAndUseLockedColors(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFocusSearch, legendItems, lockedColorsInStore, serverTitles]);

  const latest = {
    focusSearch,
    legendItems,
    searchRequest,
    isServerSearchable,
    binSearchOptions,
    publish: setFilteredItemsAndUseLockedColors,
    onEnterSelect
  };
  const latestRef = useRef(latest);
  latestRef.current = latest;

  // Recompute the filtered set from the LIVE (non-debounced) focusSearch and
  // publish it before an action (Enter / header checkbox) that must not act on
  // a stale, pre-debounce filtered set. Awaits the server result for that term
  // (reusing an in-flight one) so the action sees the same set the user will see.
  function flushFilteredItems(): LegendItemModel[] | Promise<LegendItemModel[]> {
    // Synchronous without a server search so the caller's action order matches the client-only flow.
    if (!latestRef.current.isServerSearchable(latestRef.current.focusSearch)) {
      const filtered = applySearch(
        latestRef.current.focusSearch,
        latestRef.current.legendItems,
        undefined
      );
      latestRef.current.publish(filtered);
      return filtered;
    }
    return flushServerFilteredItems();
  }

  async function flushServerFilteredItems(): Promise<LegendItemModel[]> {
    let titles: string[] | null | undefined;
    let term: string;
    let request: LegendSearchRequest | undefined;
    do {
      // Read the latest render's inputs each pass: the term or filter can change mid-await.
      const current = latestRef.current;
      term = current.focusSearch;
      request = current.searchRequest;
      titles = undefined;
      if (current.isServerSearchable(term)) {
        try {
          titles = await queryClient.fetchQuery(current.binSearchOptions(term));
        } catch {
          titles = undefined;
        }
      }
    } while (
      latestRef.current.focusSearch !== term ||
      latestRef.current.searchRequest !== request
    );
    const filtered = applySearch(term, latestRef.current.legendItems, titles);
    latestRef.current.publish(filtered);
    return filtered;
  }

  // An Enter waiting on bin-search selects only if neither the search nor the
  // focus changed meanwhile (Escape and a retype included) and the legend is
  // still mounted; a filter refresh for the same search keeps it.
  const pendingEnterRef = useRef<Nullable<object>>(null);
  useEffect(
    () => () => {
      pendingEnterRef.current = null;
    },
    [focusSearch, groupBy.property]
  );

  // Escape clears the search; Enter bulk-selects everything currently
  // filtered — matching the header checkbox, which already bulk-checks the
  // same matched `filteredItems` in one click with no confirmation.
  // `isComposing` excludes the Enter that confirms an IME composition, which
  // isn't a "submit".
  const handleFocusSearchKeydown =
    () => (e: { key: string; nativeEvent?: { isComposing?: boolean } }) => {
      if (e.key === "Escape") {
        setFocusSearch("");
      }
      if (e.key === "Enter" && !e.nativeEvent?.isComposing && focusSearch) {
        const flushed = flushFilteredItems();
        if (Array.isArray(flushed)) {
          if (flushed.length) onEnterSelect();
          return;
        }
        const token = {};
        pendingEnterRef.current = token;
        void flushed.then((filtered) => {
          if (pendingEnterRef.current !== token) return;
          pendingEnterRef.current = null;
          if (filtered.length) latestRef.current.onEnterSelect();
        });
      }
    };

  // Self-contained — derived purely from this hook's own legendItems state, so
  // (unlike the P6 dispatch effect, which also needs deps.showGroupsNotInFilter
  // and stays in the controller) these have an obvious home here.
  const hasCheckedItems = useMemo(
    () =>
      !!(
        legendItems?.length &&
        legendItems.some((item) => item.isChecked && !item.isNotEqual)
      ) || collapsedCheckedItemsRef.current.some((item) => !item.isNotEqual),
    [legendItems]
  );
  const hasEmptyBin = useMemo(
    () => !!(legendItems?.length && legendItems.some((item) => item.count === 0)),
    [legendItems]
  );

  // Built like a legend row so a value with no row yet is checked through the same path.
  const buildMemberItem = useCallback(
    (member: { title: string; count: number }): LegendItemModel => {
      const text = `(${member.count}) ${member.title}`;
      const {
        legendItems: [item]
      } = toLegendItem(
        [
          {
            groupTitle: text,
            title: text,
            inFilter: true,
            count: member.count,
            color: ""
          }
        ],
        groupBy,
        [],
        groupByTitle,
        Object.assign({}, normalizeBy, { useNormalizeBy })
      );
      return item;
    },
    [groupBy, groupByTitle, normalizeBy, useNormalizeBy]
  );

  return {
    legendItems,
    filteredItems,
    legendTitle,
    legendDisplayTitle,
    focusSearch,
    setFocusSearch,
    isSearchPending,
    handleFocusSearchKeydown,
    flushFilteredItems,
    hasCheckedItems,
    collapsedCheckedItemsRef,
    hasEmptyBin,
    buildMemberItem
  };
};
